import useTranslation from 'next-translate/useTranslation';
import { Controller, useForm } from 'react-hook-form';

import { Translate } from '@core/constant';
import { useValidator } from '@core/hook';
import { DateForm, Datetime, SelectChangeEvent, dateFormValidator } from '@core/type';

import { FieldError } from '../../input/FieldError';
import { Box } from '../../layout';
import { SelectTextControl } from './SelectTextControl';
import { SelectControlProps } from './interface-form-select';

const DAY_IN_MONTH = 31;
const MONTH_IN_YEAR = 12;
const MAJORITY = 18;
const MAX_YEARS = 101;
const LAST_1_DIGIT = 9;

export const DatePickerControl = ({
  control,
  initialValue,
  isSubmitted,
}: SelectControlProps & { initialValue?: Datetime; isSubmitted?: boolean }) => {
  const { t } = useTranslation(Translate.common.FORM);

  const { control: localControl, getValues } = useForm<DateForm>({
    mode: 'onChange',
    resolver: useValidator(dateFormValidator, t),
    defaultValues: {
      day: initialValue ? get2digit(new Date(initialValue).getDate()) : null,
      month: initialValue ? get2digit(new Date(initialValue).getMonth() + 1) : null,
      year: initialValue ? get2digit(new Date(initialValue).getFullYear()) : null,
    },
  });

  return (
    <Controller
      name={'birthday'}
      control={control}
      render={({ field, fieldState }) => {
        const handleChangeDay = ({ target: { value } }: SelectChangeEvent<string>) => {
          const values = getValues();
          if (value && values.month && values.year) {
            field.onChange(`${values.year}-${get2digit(+values.month)}-${value}`);
          }
        };
        const handleChangeMonth = ({ target: { value } }: SelectChangeEvent<string>) => {
          const values = getValues();
          if (value && values.day && values.year) {
            field.onChange(`${values.year}-${get2digit(+value)}-${values.day}`);
          }
        };
        const handleChangeYear = ({ target: { value } }: SelectChangeEvent<string>) => {
          const values = getValues();
          if (value && values.month && values.day) {
            field.onChange(`${value}-${get2digit(+values.month)}-${values.day}`);
          }
        };

        return (
          <>
            <Box display="flex" gap="8px">
              <Box flex={1}>
                <SelectTextControl
                  name="day"
                  flex={1}
                  label={t('birthday.day.label')}
                  control={localControl}
                  onChange={handleChangeDay}
                  isError={!!fieldState?.error && isSubmitted}
                  fieldState={null}
                  options={Array(DAY_IN_MONTH)
                    .fill(0)
                    .map((_, i) => ({
                      value: get2digit(i + 1),
                      label: i + 1,
                    }))}
                ></SelectTextControl>
              </Box>
              <Box flex={1}>
                <SelectTextControl
                  name="month"
                  control={localControl}
                  onChange={handleChangeMonth}
                  label={t('birthday.month.label')}
                  isError={!!fieldState?.error && isSubmitted}
                  fieldState={null}
                  options={Array(MONTH_IN_YEAR)
                    .fill(0)
                    .map((_, i) => ({
                      value: get2digit(i + 1),
                      label: i + 1,
                    }))}
                ></SelectTextControl>
              </Box>
              <Box flex={1}>
                <SelectTextControl
                  name="year"
                  control={localControl}
                  onChange={handleChangeYear}
                  label={t('birthday.year.label')}
                  isError={!!fieldState?.error && isSubmitted}
                  fieldState={null}
                  isSubmitted={isSubmitted}
                  options={Array(MAX_YEARS - MAJORITY)
                    .fill(0)
                    .map((_, i) => {
                      const firstYear = new Date().getFullYear() - MAJORITY;
                      return { value: (firstYear - i).toString(), label: firstYear - i };
                    })}
                ></SelectTextControl>
              </Box>
            </Box>
            <FieldError fieldState={fieldState} isSubmitted={isSubmitted} />
          </>
        );
      }}
    />
  );
};

const get2digit = (i: number) => (i <= LAST_1_DIGIT ? `0${i}` : i.toString());
